import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { apiErrorText } from '../api/errorText';
import type { CurrentUser } from '../api/types';
import type { UserRole } from '../api/types';
import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import AsyncSection from '../components/AsyncSection';

const roles: UserRole[] = ['User', 'SuperAdmin'];

/// Who may use this server. The role decides the permissions; the server is the one that
/// enforces them, and it refuses to remove the last administrator.
export default function UsersPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const users = useApi(['users'], api.users);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('User');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  /// Whose password an administrator is setting, and what they are setting it to. Nobody is
  /// asked for the old one here - an administrator does not have it, which is the point.
  const [resetting, setResetting] = useState<CurrentUser>();
  const [replacement, setReplacement] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    try {
      await api.createUser({ name, password, role });
      setName('');
      setPassword('');
      setRole('User');
      users.reload();
    } catch (caught) {
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  const setRoleOf = async (userId: string, role: UserRole) => {
    setBusy(true);
    setError(undefined);

    try {
      await api.setUserRole(userId, role);
      users.reload();
    } catch (caught) {
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  const setPasswordOf = async () => {
    if (!resetting) {
      return;
    }

    setBusy(true);
    setError(undefined);

    try {
      await api.setUserPassword(resetting.id, replacement);
      setResetting(undefined);
      setReplacement('');
    } catch (caught) {
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    setError(undefined);

    try {
      await api.deleteUser(userId);
      users.reload();
    } catch (caught) {
      // The server refuses to delete the last administrator, and says why.
      const text = apiErrorText(caught, t);
      setError([text.message, text.remedy].filter(Boolean).join(' '));
    }
  };

  return (
    <Stack spacing={3}>
      {/* A tab of the settings, under their title: a section, not a second page title. */}
      <Typography variant="h3" component="h3">{t('users.title')}</Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <AsyncSection loading={users.loading} error={users.error} onRetry={users.reload}>
        <Stack spacing={1.5}>
          {users.data?.map((user) => (
            <Card key={user.id} variant="outlined">
              {/* On a phone the name goes above and what can be done to it below, because
                  three controls beside a name is more than four hundred pixels hold. */}
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1.5, sm: 2 },
                  py: 1.5,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                    {user.name}
                    {user.id === currentUser?.id && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ marginInlineStart: 1 }}>
                        {t('users.you')}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('users.permissionCount', { permissions: user.permissions.length })}
                  </Typography>
                </Box>

                {/* The role is the permissions: what one grants is declared by the server and
                    shown beside the count above, so this is the whole of it. */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
                >
                <TextField
                  select
                  size="small"
                  aria-label={t('users.role')}
                  value={user.role}
                  disabled={busy}
                  onChange={(event) => void setRoleOf(user.id, event.target.value as UserRole)}
                  sx={{ minWidth: { xs: 0, sm: 170 }, flexGrow: { xs: 1, sm: 0 } }}
                >
                  {roles.map((candidate) => (
                    <MenuItem key={candidate} value={candidate}>
                      {t(`account.role.${candidate}`)}
                    </MenuItem>
                  ))}
                </TextField>

                <Button size="small" onClick={() => setResetting(user)} sx={{ whiteSpace: 'nowrap' }}>
                  {t('users.setPassword')}
                </Button>

                <IconButton
                  size="small"
                  aria-label={t('users.delete')}
                  onClick={() => void remove(user.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </AsyncSection>

      <Dialog open={resetting !== undefined} onClose={() => setResetting(undefined)} fullWidth maxWidth="xs">
        <DialogTitle>{t('users.setPasswordFor', { name: resetting?.name ?? '' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('users.setPasswordHint')}
            </Typography>

            <TextField
              autoFocus
              label={t('users.password')}
              type="password"
              autoComplete="new-password"
              value={replacement}
              onChange={(event) => setReplacement(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetting(undefined)}>{t('common.close')}</Button>
          <Button variant="contained" disabled={replacement.length === 0 || busy} onClick={() => void setPasswordOf()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Card component="form" onSubmit={submit} variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h3">{t('users.add')}</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('users.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="off"
              />
              <TextField
                label={t('users.password')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type="password"
                autoComplete="new-password"
              />
              <TextField
                select
                label={t('users.role')}
                value={role}
                onChange={(event) => setRole(event.target.value as UserRole)}
                sx={{ minWidth: 180 }}
              >
                {roles.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`account.role.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Box>
              <Button type="submit" variant="contained" disabled={busy || name.length < 2 || password.length < 8}>
                {t('common.create')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
